import { mm } from "@inovitas/infra3dsdk";
import { ChevronLeft, Delete, RestoreFromTrash } from "@mui/icons-material";
import { Box, IconButton, Typography } from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import getChangedFeaturesList from "shared/common/utils/layers/getChangedFeaturesList";
import SingleFeatureTable from "shared/ui/toolbar/tools/layertreeAndEdit/featureTable/SingleFeatureTable";

interface props {
  rows: mm.Feature[];
}

export default function EditFeatureList({ rows }: props) {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);

  const editToolActivated = sidebarStore((state) => state.editToolActivated);
  const editPopover = sidebarStore((state) => state.editPopover);
  const changeFeatureTableAddedRows = sidebarStore(
    (state) => state.changeFeatureTableAddedRows
  );

  const [nonSelectedGeometryIds, setNonSelectedGeometryIds] = useState<
    Set<number>
  >(new Set());

  // Toggle the expanded state so multiple features can be selected at once,
  // while keeping the inverse set of non-selected features.
  const toggleFeatureSelection = (feature: mm.Feature) => {
    setNonSelectedGeometryIds((currentIds) => {
      const nextIds = new Set(currentIds);

      if (nextIds.has(feature.geometry.id)) {
        nextIds.delete(feature.geometry.id);
      } else {
        nextIds.add(feature.geometry.id);
      }

      return nextIds;
    });
  };

  //delete feature function
  const deleteFeature = (item: mm.Feature) => {
    setNonSelectedGeometryIds((currentIds) => {
      const nextIds = new Set(currentIds);
      nextIds.delete(item.geometry.id);
      return nextIds;
    });

    if (viewer == undefined) return;
    const layer = viewer.getScene().getLayerHandler().getActiveLayer();
    if (layer === null) return;
    if (editToolActivated === "add") {
      layer.getLayerEditor().featuresinsert.destroyFeature(item, true);
    } else if (editToolActivated === "edit") {
      delete layer.getLayerEditor().featuresupdate[item.id];
      layer.featureselector.clearSelection();
      layer.reload();
    }
    changeFeatureTableAddedRows(getChangedFeaturesList(layer));
  };

  // restore deleted features
  const restoreFeature = (item: mm.Feature) => {
    setNonSelectedGeometryIds((currentIds) => {
      const nextIds = new Set(currentIds);
      nextIds.delete(item.geometry.id);
      return nextIds;
    });

    if (viewer == undefined) return;
    const layer = viewer.getScene().getLayerHandler().getActiveLayer();
    if (layer === null) return;
    layer.getLayerEditor().featuresdelete = layer
      .getLayerEditor()
      .featuresdelete.filter((id: string | number) => id !== item.id);
    layer.getFeatureById(item.id).show();
    layer.reload();
    changeFeatureTableAddedRows(getChangedFeaturesList(layer));
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column-reverse",
      }}
    >
      {rows !== undefined &&
        rows.map((item: mm.Feature, index: number) => {
          const featureTableHidden = nonSelectedGeometryIds.has(
            item.geometry.id
          );

          return (
            <React.Fragment key={index}>
              {item && (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{
                      paddingX: 1,
                      paddingY: 0.5,
                      display: "flex",
                      flexDirection: "row",
                      gap: 1,
                      alignItems: "center",
                      backgroundColor:
                        index % 2 === 0
                          ? darkTheme
                            ? "primary.light" // Could this not be simplified? 3
                            : "grey.light" // Could this not be simplified? 4
                          : "",
                    }}
                  >
                    <Typography
                      onClick={() => toggleFeatureSelection(item)}
                      sx={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: darkTheme ? "white" : "darkblue.main",
                        "@media (hover: hover)": {
                          ":hover": {
                            cursor: "pointer",
                            color: "secondary.main",
                          },
                        },
                      }}
                    >
                      {item.id
                        ? item.id
                        : `${t("edit_layers.new_feature")} ${index + 1}`}
                    </Typography>
                    {editToolActivated !== "delete" && (
                      <IconButton
                        onClick={() => deleteFeature(item)}
                        size="small"
                        sx={{
                          padding: 0,
                          marginLeft: "auto",
                          color: darkTheme ? "white" : "darkblue.main",
                          "@media (hover: hover)": {
                            ":hover": { color: "secondary.main" },
                          },
                        }}
                      >
                        <Delete />
                      </IconButton>
                    )}
                    {editToolActivated === "delete" && editPopover && (
                      <IconButton
                        onClick={() => restoreFeature(item)}
                        size="small"
                        sx={{
                          padding: 0,
                          marginLeft: "auto",
                          color: darkTheme ? "white" : "darkblue.main",
                          "@media (hover: hover)": {
                            ":hover": { color: "secondary.main" },
                          },
                        }}
                      >
                        <RestoreFromTrash />
                      </IconButton>
                    )}
                    <IconButton
                      onClick={() => toggleFeatureSelection(item)}
                      size="small"
                      sx={{
                        padding: 0,
                        color: darkTheme ? "white" : "darkblue.main",
                        "@media (hover: hover)": {
                          ":hover": { color: "secondary.main" },
                        },
                        rotate: featureTableHidden ? "-90deg" : "90deg",
                        transition: "rotate 0.2s",
                      }}
                    >
                      <ChevronLeft />
                    </IconButton>
                  </Box>
                  <Box
                    sx={{
                      paddingRight: 1,
                      position: "relative",
                      overflow: "auto",

                      maxHeight: featureTableHidden ? "0px" : "200px",

                      transition: "max-height 0.3s",
                    }}
                  >
                    {!featureTableHidden && (
                      <SingleFeatureTable
                        selectedTableRows={{
                          ...item.toGeoJSON(),
                          uniqueID: index,
                        }}
                      />
                    )}
                  </Box>
                </Box>
              )}
            </React.Fragment>
          );
        })}
    </Box>
  );
}
